import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { phase1Api } from "../api/client";

function formatCategory(value) {
  return String(value || "Platform").replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());
}

function timeLabel(value) {
  if (!value) return "Now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Now";
  const diff = Math.max(0, Date.now() - date.getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  async function loadSummary() {
    try {
      const res = await phase1Api.notificationSummary();
      setItems(res.data?.latest || []);
      setUnread(res.data?.unread_count || 0);
    } catch (error) {
      // Keep the topbar clean if notifications are temporarily unavailable.
    }
  }

  useEffect(() => {
    loadSummary();
    const timer = setInterval(loadSummary, 45000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    function handleClickAway(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    }
    function handleEscape(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClickAway);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickAway);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  async function openNotification(item) {
    try {
      if (!item.is_read) await phase1Api.markNotificationRead(item.id);
    } catch (error) {
      // Navigation should still work even if mark-as-read fails.
    }
    setOpen(false);
    setUnread((value) => Math.max(0, value - (item.is_read ? 0 : 1)));
    navigate("/portal/notifications");
  }

  async function markAllRead() {
    setLoading(true);
    try {
      await phase1Api.markAllNotificationsRead();
      setUnread(0);
      setItems((current) => current.map((item) => ({ ...item, is_read: true })));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="notification-bell-wrap" ref={wrapperRef}>
      <button
        type="button"
        className="notification-bell-btn"
        onClick={() => {
          setOpen((value) => !value);
          loadSummary();
        }}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="notification-bell-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z"/><path d="M10 21h4"/><path d="M9.5 17a2.5 2.5 0 0 0 5 0"/></svg></span>
        {unread > 0 && <strong>{unread > 99 ? "99+" : unread}</strong>}
      </button>

      {open && (
        <div className="notification-popover" role="dialog" aria-label="Recent notifications">
          <div className="notification-popover-head">
            <div>
              <span>Notifications</span>
              <strong>{unread} unread</strong>
            </div>
            {unread > 0 && <button type="button" onClick={markAllRead} disabled={loading}>Mark all read</button>}
          </div>

          <div className="notification-popover-list">
            {items.length === 0 ? (
              <p className="notification-empty">No notifications yet.</p>
            ) : items.map((item) => (
              <button type="button" key={item.id} className={`notification-mini-card ${item.is_read ? "is-read" : "is-unread"}`} onClick={() => openNotification(item)}>
                <span>{formatCategory(item.category)}</span>
                <strong>{item.title}</strong>
                <em>{item.message}</em>
                <small>{timeLabel(item.created_at)}</small>
              </button>
            ))}
          </div>

          <Link to="/portal/notifications" className="notification-view-all" onClick={() => setOpen(false)}>View all notifications</Link>
        </div>
      )}
    </div>
  );
}
